import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, providersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, ArrowRight, Code2, Smartphone, Server, Bot, GitBranch, Check, Terminal, Loader2, Sparkles, Database, Layers, FileCode, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const projectTypes = [
  { id: 'fullstack', name: 'Full Stack App', desc: 'Web application with frontend + backend', icon: Code2, color: 'indigo' },
  { id: 'mobile', name: 'Mobile App', desc: 'iOS / Android / Cross-platform', icon: Smartphone, color: 'cyan' },
  { id: 'service', name: 'API Service', desc: 'Backend service / microservice', icon: Server, color: 'emerald' },
  { id: 'ai_agent', name: 'AI Agent', desc: 'Agents, tools, and pipelines', icon: Bot, color: 'purple' },
  { id: 'pipeline', name: 'Data Pipeline', desc: 'ETL / data processing workflow', icon: GitBranch, color: 'amber' },
];

export default function NewProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExisting = searchParams.get('type') === 'existing';

  const [step, setStep] = useState(isExisting ? 0 : 1);
  const [projectMode, setProjectMode] = useState(isExisting ? 'existing' : 'new');
  const [selectedType, setSelectedType] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-5.2');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [repoAnalysis, setRepoAnalysis] = useState(null);

  useEffect(() => {
    providersAPI.listActive().then(res => setProviders(res.data)).catch(() => {});
  }, []);

  const currentProviderModels = providers.find(p => p.provider_key === selectedProvider)?.models || [];

  const handleAnalyzeRepo = async () => {
    if (!repoUrl.trim()) {
      toast.error('Enter a repository URL');
      return;
    }
    
    // Infer project type and name from URL
    const urlParts = repoUrl.replace(/\.git$/, '').split('/');
    const inferredName = urlParts[urlParts.length - 1] || 'My Project';
    if (!name) setName(inferredName.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    
    // Auto-detect project type from common patterns
    const lowerUrl = repoUrl.toLowerCase();
    if (!selectedType) {
      if (lowerUrl.includes('mobile') || lowerUrl.includes('ios') || lowerUrl.includes('android') || lowerUrl.includes('react-native') || lowerUrl.includes('flutter')) {
        setSelectedType('mobile');
      } else if (lowerUrl.includes('api') || lowerUrl.includes('service') || lowerUrl.includes('backend')) {
        setSelectedType('service');
      } else if (lowerUrl.includes('agent') || lowerUrl.includes('ai') || lowerUrl.includes('ml') || lowerUrl.includes('llm')) {
        setSelectedType('ai_agent');
      } else if (lowerUrl.includes('pipeline') || lowerUrl.includes('etl') || lowerUrl.includes('data')) {
        setSelectedType('pipeline');
      } else {
        setSelectedType('fullstack');
      }
    }
    
    // Create project first, then analyze
    setAnalyzing(true);
    try {
      const createRes = await projectAPI.create({
        name: name || inferredName,
        description: description || `Imported from ${repoUrl}`,
        project_type: selectedType || 'fullstack',
        llm_provider: selectedProvider,
        llm_model: selectedModel,
      });
      
      const projectId = createRes.data.id;
      
      // Now analyze the repo
      const analysisRes = await projectAPI.analyzeRepo(projectId, { repo_url: repoUrl });
      setRepoAnalysis(analysisRes.data.analysis);
      
      toast.success('Repository analyzed successfully!');
      
      // Navigate to project after brief delay
      setTimeout(() => {
        navigate(`/project/${projectId}`);
      }, 2000);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Project name required'); return; }
    if (!selectedType) { toast.error('Select project type'); return; }
    setLoading(true);
    try {
      const res = await projectAPI.create({
        name, description, project_type: selectedType,
        llm_provider: selectedProvider, llm_model: selectedModel,
      });
      if (projectMode === 'existing' && repoUrl) {
        await projectAPI.connectRepo({ repo_url: repoUrl, project_id: res.data.id });
      }
      toast.success('Project created!');
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="new-project-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center">
          <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <span className="ml-4 text-sm text-white/40">/ New Project</span>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-white tracking-tight mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}} data-testid="new-project-title">
          {projectMode === 'existing' ? 'Connect Existing Project' : 'Create New Project'}
        </h1>
        <p className="text-white/40 mb-8">Set up your project in a few steps</p>

        {/* Step 0: New or Existing */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-mode">
            <Tabs value={projectMode} onValueChange={setProjectMode} className="w-full">
              <TabsList className="bg-white/5 border border-white/10 w-full">
                <TabsTrigger value="new" className="flex-1 data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-white/50" data-testid="tab-new">New Project</TabsTrigger>
                <TabsTrigger value="existing" className="flex-1 data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-white/50" data-testid="tab-existing">Existing Project</TabsTrigger>
              </TabsList>
            </Tabs>
            {projectMode === 'existing' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-white/60 text-sm">GitHub Repository URL</Label>
                  <Input 
                    value={repoUrl} 
                    onChange={(e) => setRepoUrl(e.target.value)} 
                    className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" 
                    placeholder="https://github.com/user/repo" 
                    data-testid="repo-url-input" 
                  />
                  <p className="text-xs text-white/30 mt-2">We'll analyze your repo with AI and generate requirements, detect tech stack, and suggest improvements</p>
                </div>
                
                {/* Analysis Results */}
                {repoAnalysis && (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4 space-y-4" data-testid="analysis-results">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">Analysis Complete!</span>
                    </div>
                    
                    {/* Tech Stack */}
                    {repoAnalysis.tech_stack && (
                      <div>
                        <p className="text-xs text-white/40 mb-2">Detected Tech Stack</p>
                        <div className="flex flex-wrap gap-2">
                          {repoAnalysis.tech_stack.frontend && (
                            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                              <Code2 className="w-3 h-3 mr-1" /> {repoAnalysis.tech_stack.frontend}
                            </Badge>
                          )}
                          {repoAnalysis.tech_stack.backend && (
                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                              <Server className="w-3 h-3 mr-1" /> {repoAnalysis.tech_stack.backend}
                            </Badge>
                          )}
                          {repoAnalysis.tech_stack.database && (
                            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                              <Database className="w-3 h-3 mr-1" /> {repoAnalysis.tech_stack.database}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Architecture */}
                    {repoAnalysis.architecture && (
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-white/40" />
                        <span className="text-xs text-white/60">Architecture:</span>
                        <Badge variant="outline" className="text-white/60 border-white/20 capitalize">{repoAnalysis.architecture}</Badge>
                      </div>
                    )}
                    
                    {/* Summary */}
                    {repoAnalysis.summary && (
                      <p className="text-xs text-white/50">{repoAnalysis.summary}</p>
                    )}
                    
                    <p className="text-xs text-emerald-400">Redirecting to project workspace...</p>
                  </div>
                )}
                
                {/* Analyze Button */}
                {!repoAnalysis && (
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white w-full" 
                    onClick={handleAnalyzeRepo}
                    disabled={analyzing || !repoUrl.trim()}
                    data-testid="analyze-repo-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing Repository...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze with AI
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
            {projectMode === 'new' && (
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => setStep(1)} data-testid="step0-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        )}

        {/* Step 1: Project Type */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-type">
            <p className="text-sm font-medium tracking-wider uppercase text-white/30">Select Project Type</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectTypes.map((type) => (
                <Card
                  key={type.id}
                  className={`bg-white/[0.03] border cursor-pointer transition-all hover:-translate-y-0.5 ${selectedType === type.id ? 'border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.2)]' : 'border-white/10 hover:border-white/20'}`}
                  onClick={() => setSelectedType(type.id)}
                  data-testid={`type-${type.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <type.icon className={`w-8 h-8 ${selectedType === type.id ? 'text-indigo-400' : 'text-white/30'}`} />
                      {selectedType === type.id && <Check className="w-5 h-5 text-indigo-400" />}
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1">{type.name}</h3>
                    <p className="text-xs text-white/30">{type.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex gap-3">
              {isExisting && <Button variant="outline" className="border-white/20 text-white" onClick={() => setStep(0)}>Back</Button>}
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => { if (!selectedType) { toast.error('Select a type'); return; } setStep(2); }} data-testid="step1-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Project Details + LLM */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/60 text-sm">Project Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" placeholder="My Awesome Project" data-testid="project-name-input" />
              </div>
              <div>
                <Label className="text-white/60 text-sm">Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" placeholder="Brief description" data-testid="project-desc-input" />
              </div>
            </div>

            <div className="border-t border-white/5 pt-6">
              <p className="text-sm font-medium tracking-wider uppercase text-white/30 mb-4">LLM Configuration</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-white/60 text-sm">Provider</Label>
                  <Select value={selectedProvider} onValueChange={(val) => { setSelectedProvider(val); setSelectedModel(''); }} data-testid="provider-select">
                    <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1.5" data-testid="provider-select-trigger">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                      {providers.map(p => (
                        <SelectItem key={p.provider_key} value={p.provider_key} className="text-white focus:bg-white/5 focus:text-white">{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/60 text-sm">Model</Label>
                  <Select value={selectedModel} onValueChange={setSelectedModel} data-testid="model-select">
                    <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1.5" data-testid="model-select-trigger">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                      {currentProviderModels.map(m => (
                        <SelectItem key={m} value={m} className="text-white focus:bg-white/5 focus:text-white">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="border-white/20 text-white" onClick={() => setStep(1)} data-testid="step2-back-btn">Back</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_20px_rgba(99,102,241,0.3)]" onClick={handleCreate} disabled={loading} data-testid="create-project-btn">
                {loading ? 'Creating...' : 'Create Project'} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
